import type { RagSources, ToolCall } from '@kanbu/schema';
import {
  blocksOf,
  formValueSchema,
  inferToolPresentation,
  kanbuNonStandardValueSchema,
  sourcesFromAnnotations,
  type ContentBlock,
  type FormBlockValue,
  type ToolPresentation,
} from '@kanbu/schema/contracts';
import {
  SourcesDisplayMode,
  type SourcesFormat,
  ToolType,
} from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { Fragment, memo, useMemo, type ReactNode } from 'react';

import { Message } from './message';
import { ReasoningBlock } from './reasoning-block';
import {
  ToolProvider,
  type RenderUnknownTool,
  type ToolRegistry,
} from '../../tools';
import { ActivityTrail } from '../../tools/components/activity-trail';
import type { ChatMessageItem } from '../../types';
import { Markdown } from '../../ui';
import { SourcesBlock } from '../sources';

export interface MessageBlocksProps {
  message: ChatMessageItem;
  toolRegistry?: ToolRegistry;
  renderUnknownTool?: RenderUnknownTool;
  showToolActivity?: boolean;
  expandToolActivity?: boolean;
  debugToolCalls?: boolean;
  renderTrailStepInspector?: (toolCall: ToolCall) => ReactNode;
  showReasoning?: boolean;
  renderSources?: (message: ChatMessageItem) => React.ReactNode;
  sourcesFormat?: SourcesFormat;
  sourcesDisplayMode?: SourcesDisplayMode;
  inlineCitations?: boolean;
  answerFooter?: ReactNode;
}

interface ResolvedToolCall {
  id: string;
  name: string;
  args: Record<string, unknown>;
  content?: string;
  artifact?: unknown;
  status?: ToolCall['status'];
  label?: string;
  index: number;
  presentation: ToolPresentation;
}

function toolCallsById(message: ChatMessageItem): Map<string, ToolCall> {
  const map = new Map<string, ToolCall>();

  for (const call of message.toolCalls ?? []) {
    if (call.type === ToolType.Tool) {
      map.set(call.id, call);
    }
  }

  return map;
}

function presentationForTool(params: {
  name: string;
  fromCall?: ToolCall;
  fromResult?: ContentBlock & { type: 'tool_result' };
}): ToolPresentation {
  const { name, fromCall, fromResult } = params;

  if (fromCall?.display) {
    return {
      display: fromCall.display,
      kind: fromCall.kind ?? inferToolPresentation(name).kind,
    };
  }

  if (fromResult?.display) {
    return {
      display: fromResult.display,
      kind: fromResult.kind ?? inferToolPresentation(name).kind,
    };
  }

  return inferToolPresentation(name);
}

function resultStatus(
  resultBlock?: Extract<ContentBlock, { type: 'tool_result' }>,
): ToolCall['status'] {
  if (!resultBlock) {
    return 'pending';
  }

  return resultBlock.is_error ? 'error' : 'completed';
}

/**
 * While the assistant is still streaming we keep unfinished presentation
 * cards visible (the "Preparing…" spinner). Once the turn settles — or on
 * persisted rows with no stream status — an unfinished non-form card is an
 * orphan and must not keep spinning beside a completed sibling.
 */
function isMessageSettled(status: ChatMessageItem['status']): boolean {
  return status !== 'start' && status !== 'streaming';
}

function isSettledUnfinishedCard(tool: ResolvedToolCall): boolean {
  return (
    tool.presentation.display === 'card' &&
    tool.presentation.kind !== 'form' &&
    tool.status !== 'completed' &&
    tool.status !== 'error' &&
    !tool.content &&
    tool.artifact === undefined
  );
}

function resolveToolCall(
  message: ChatMessageItem,
  toolCallId: string,
  callBlock?: Extract<ContentBlock, { type: 'tool_call' }>,
  resultBlock?: Extract<ContentBlock, { type: 'tool_result' }>,
): ResolvedToolCall | null {
  const live = toolCallsById(message).get(toolCallId);
  const name = live?.name ?? callBlock?.name ?? resultBlock?.name ?? '';

  if (!name && !live) {
    return null;
  }

  const presentation = presentationForTool({
    name,
    fromCall: live,
    fromResult: resultBlock,
  });

  return {
    id: toolCallId,
    name,
    args: (live?.args ?? callBlock?.args ?? {}) as Record<string, unknown>,
    content:
      typeof live?.content === 'string' ? live.content : resultBlock?.content,
    artifact: live?.artifact ?? resultBlock?.artifact,
    /**
     * No result block and no live call means the call never returned —
     * an interrupted or crashed turn. Reporting `completed` there renders
     * it as a settled-but-empty card.
     */
    status: live?.status ?? resultStatus(resultBlock),
    label: live?.label,
    index: live?.index ?? 0,
    presentation,
  };
}

function isRenderableBlock(block: ContentBlock): boolean {
  switch (block.type) {
    case 'text':
      return Boolean(block.text?.trim());
    case 'reasoning':
      return Boolean(block.reasoning?.trim());
    case 'tool_call':
    case 'tool_call_chunk':
    case 'invalid_tool_call':
    case 'tool_result':
      return true;
    case 'non_standard': {
      const parsed = kanbuNonStandardValueSchema.safeParse(block.value);

      return parsed.success;
    }

    default:
      return false;
  }
}

/**
 * Whether this message produced anything at all. Config-independent: the
 * stream-error path uses it to tell a partial answer from an empty shell,
 * and a turn that only got as far as a tool call is not an empty shell
 * even when nothing about it is displayed.
 *
 * For "is anything on screen right now" use {@link messageHasVisibleContent} —
 * these answer different questions and must not be collapsed again.
 */
export function messageHasRenderableBlocks(message: ChatMessageItem): boolean {
  if (message.sources?.length) {
    return true;
  }

  return blocksOf(message).some(isRenderableBlock);
}

/**
 * Whether the renderer would actually draw something for this message
 * under the given display config — the question the typing bubble needs
 * answered.
 *
 * `messageHasRenderableBlocks` is true as soon as any block exists, but
 * reasoning is gated behind `showReasoning` and the activity trail behind
 * `showToolActivity`. With both off (the default) a turn that opens with
 * reasoning or a tool call flipped the typing bubble off while rendering
 * nothing in its place, so the indicator vanished and only reappeared when
 * text began to stream.
 */
export function messageHasVisibleContent(
  message: ChatMessageItem,
  options: {
    showReasoning?: boolean;
    showToolActivity?: boolean;
    debugToolCalls?: boolean;
  } = {},
): boolean {
  const { showReasoning, showToolActivity, debugToolCalls = false } = options;

  if (message.sources?.length) {
    return true;
  }

  const { reasoning, process, answer } = partitionBlocks({
    message,
    blocks: blocksOf(message),
    debugToolCalls,
  });

  if (answer.length > 0) {
    return true;
  }

  if (showReasoning && reasoning.trim()) {
    return true;
  }

  return Boolean((showToolActivity || debugToolCalls) && process.length > 0);
}

export function messageHasAnswerContent(message: ChatMessageItem): boolean {
  /**
   * Match what {@link partitionBlocks} puts in the answer bubble: any text
   * block. `answerBlockOf` only keeps text after the last tool, so narration
   * before a tool (or a hybrid row) would leave the activity trail header
   * spinning even though the bubble already shows copy.
   */
  return blocksOf(message).some(
    block => block.type === 'text' && Boolean(block.text?.trim()),
  );
}

function renderToolCard(params: {
  tool: ResolvedToolCall;
  message: ChatMessageItem;
  toolRegistry?: ToolRegistry;
  renderUnknownTool?: RenderUnknownTool;
  debugToolCalls: boolean;
}) {
  const { tool, message, toolRegistry, renderUnknownTool, debugToolCalls } =
    params;

  if (tool.presentation.display === 'hidden') {
    return null;
  }

  if (tool.presentation.kind === 'form') {
    return renderUnknownTool?.({
      toolName: tool.name,
      toolCallId: tool.id,
      args: tool.args,
      content: tool.content,
      artifact: tool.artifact,
      toolCallStatus: tool.status,
      kind: tool.presentation.kind,
      state: tool.status === 'pending' ? 'call-complete' : 'result',
    });
  }

  if (debugToolCalls || toolRegistry?.[tool.name]) {
    return (
      <ToolProvider
        status={message.status}
        args={tool.args}
        toolName={tool.name}
        index={tool.index}
        content={tool.content}
        artifact={tool.artifact}
        toolCallStatus={tool.status}
        toolCallId={tool.id}
        toolRegistry={toolRegistry ?? {}}
        renderFallback={renderUnknownTool}
        label={tool.label}
      />
    );
  }

  return renderUnknownTool?.({
    toolName: tool.name,
    toolCallId: tool.id,
    args: tool.args,
    content: tool.content,
    artifact: tool.artifact,
    toolCallStatus: tool.status,
    state: tool.status === 'error' ? 'error' : 'result',
  });
}

/**
 * The turn's process region: everything the assistant did on the way to
 * the answer. One trail for the whole message, not one per contiguous run
 * of tools — the region shows a single active line that replaces itself
 * and collapses to a summary when the turn settles.
 */
function ProcessRegion({
  tools,
  message,
  expandToolActivity,
  renderTrailStepInspector,
}: {
  tools: ResolvedToolCall[];
  message: ChatMessageItem;
  expandToolActivity: boolean;
  renderTrailStepInspector?: (toolCall: ToolCall) => ReactNode;
}) {
  const toolCalls: ToolCall[] = tools.map(tool => ({
    id: tool.id,
    type: ToolType.Tool,
    name: tool.name,
    args: tool.args,
    index: tool.index,
    content: tool.content,
    artifact: tool.artifact,
    status: tool.status,
    label: tool.label,
    display: tool.presentation.display,
    kind: tool.presentation.kind,
  }));

  return (
    <ActivityTrail
      toolCalls={toolCalls}
      status={message.status}
      hasAnswerContent={messageHasAnswerContent(message)}
      defaultExpanded={expandToolActivity}
      renderStepInspector={renderTrailStepInspector}
    />
  );
}

/** One item of the answer flow, in block order. */
type AnswerItem =
  | { kind: 'text' }
  | { kind: 'card'; tool: ResolvedToolCall }
  | {
      kind: 'form';
      value: FormBlockValue;
      result?: Extract<ContentBlock, { type: 'tool_result' }>;
    };

interface Partitioned {
  /** All reasoning, joined — the model's thinking is one train of thought. */
  reasoning: string;
  /** Activity tools, invalid calls and interrupted calls, in order. */
  process: ResolvedToolCall[];
  answer: AnswerItem[];
  /** Every text block, already joined for the single answer bubble. */
  text: string;
}

/**
 * Partitions a turn's blocks by TYPE, never by position.
 *
 * The protocol tags every block with its role, so the renderer never has
 * to decide mid-stream whether a given text block is narration or the
 * answer — that classification is what produced the promote/demote edge
 * cases this replaced. Reasoning and activity-display tools go to the
 * process region above; every `text` block concatenates into one bubble;
 * card-display results and forms render inline in the answer flow.
 */
export function partitionBlocks(params: {
  message: ChatMessageItem;
  blocks: ContentBlock[];
  debugToolCalls: boolean;
}): Partitioned {
  const { message, blocks, debugToolCalls } = params;

  const resultByCallId = new Map<
    string,
    Extract<ContentBlock, { type: 'tool_result' }>
  >();
  /**
   * A form is carried by three blocks — the model's `tool_call`, the
   * `non_standard` block holding the artifact, and the `tool_result` once
   * it is answered. The `non_standard` block owns the rendering, so its id
   * is claimed up front and the other two are skipped; without this the
   * form card renders twice the moment a result arrives.
   */
  const formCallIds = new Set<string>();

  for (const block of blocks) {
    if (block.type === 'tool_result') {
      resultByCallId.set(block.tool_call_id, block);
      continue;
    }

    if (block.type === 'non_standard') {
      const parsed = formValueSchema.safeParse(block.value);

      if (parsed.success) {
        formCallIds.add(parsed.data.toolCallId);
      }
    }
  }

  const reasoning: string[] = [];
  const process: ResolvedToolCall[] = [];
  const answer: AnswerItem[] = [];
  const seenCallIds = new Set<string>(formCallIds);
  const renderedFormIds = new Set<string>();

  let text = '';
  let brokeSinceText = false;

  const appendText = (value: string) => {
    if (text && brokeSinceText) {
      text += '\n\n';
    }

    text += value;
    brokeSinceText = false;
  };

  const placeTool = (tool: ResolvedToolCall) => {
    if (tool.presentation.display === 'hidden') {
      return;
    }

    if (tool.presentation.display === 'activity' && !debugToolCalls) {
      process.push(tool);

      return;
    }

    if (isMessageSettled(message.status) && isSettledUnfinishedCard(tool)) {
      return;
    }

    answer.push({ kind: 'card', tool });
    brokeSinceText = true;
  };

  for (const [index, block] of blocks.entries()) {
    switch (block.type) {
      case 'text':
        if (!block.text?.trim()) {
          break;
        }

        if (!text) {
          answer.push({ kind: 'text' });
        }

        appendText(block.text);

        break;

      case 'reasoning':
        if (block.reasoning?.trim()) {
          reasoning.push(block.reasoning);
        }

        break;

      case 'tool_call':
      case 'tool_result': {
        const toolCallId =
          block.type === 'tool_call' ? block.id : block.tool_call_id;

        if (!toolCallId || seenCallIds.has(toolCallId)) {
          break;
        }

        seenCallIds.add(toolCallId);

        const resolved = resolveToolCall(
          message,
          toolCallId,
          block.type === 'tool_call' ? block : undefined,
          resultByCallId.get(toolCallId),
        );

        if (resolved) {
          placeTool(resolved);
        }

        break;
      }

      /**
       * A tool call the turn never finished emitting — what sits in
       * `blocks` when a stream is interrupted mid-call. Shown as a still-
       * running step rather than silently dropped.
       */
      case 'tool_call_chunk': {
        const toolCallId = block.id ?? `chunk-${index}`;

        if (seenCallIds.has(toolCallId)) {
          break;
        }

        seenCallIds.add(toolCallId);
        process.push(pendingToolCall(toolCallId, block.name ?? ''));

        break;
      }

      /**
       * The model emitted something shaped like a tool call that could not
       * be parsed. Surfacing it as an error step is what makes a model
       * emitting tool calls as prose visible instead of silent.
       */
      case 'invalid_tool_call': {
        const toolCallId = block.id ?? `invalid-${index}`;

        if (seenCallIds.has(toolCallId)) {
          break;
        }

        seenCallIds.add(toolCallId);
        process.push(
          failedToolCall(toolCallId, block.name ?? '', block.error ?? ''),
        );

        break;
      }

      case 'non_standard': {
        const parsed = formValueSchema.safeParse(block.value);

        if (!parsed.success || renderedFormIds.has(parsed.data.toolCallId)) {
          break;
        }

        renderedFormIds.add(parsed.data.toolCallId);
        answer.push({
          kind: 'form',
          value: parsed.data,
          result: resultByCallId.get(parsed.data.toolCallId),
        });
        brokeSinceText = true;

        break;
      }

      default:
        break;
    }
  }

  /**
   * Operator handoff UI always sits under the prose: contact card + connect
   * button. Models often call the tool before writing the user-facing line;
   * without this reorder the card would land above the answer text.
   */
  const operatorCards: AnswerItem[] = [];
  const answerWithoutOperators: AnswerItem[] = [];

  for (const item of answer) {
    if (item.kind === 'card' && item.tool.presentation.kind === 'operator') {
      operatorCards.push(item);
    } else {
      answerWithoutOperators.push(item);
    }
  }

  return {
    reasoning: reasoning.join('\n\n'),
    process,
    answer: [...answerWithoutOperators, ...operatorCards],
    text,
  };
}

function pendingToolCall(id: string, name: string): ResolvedToolCall {
  return {
    id,
    name,
    args: {},
    status: 'pending',
    index: 0,
    presentation: inferToolPresentation(name),
  };
}

function failedToolCall(
  id: string,
  name: string,
  error: string,
): ResolvedToolCall {
  return {
    id,
    name,
    args: {},
    content: error,
    status: 'error',
    index: 0,
    presentation: inferToolPresentation(name),
  };
}

export const MessageBlocks = memo((props: MessageBlocksProps) => {
  const {
    message,
    toolRegistry,
    renderUnknownTool,
    showToolActivity = false,
    expandToolActivity = false,
    debugToolCalls = false,
    renderTrailStepInspector,
    showReasoning = false,
    renderSources,
    sourcesFormat,
    sourcesDisplayMode = SourcesDisplayMode.Pills,
    inlineCitations = false,
    answerFooter,
  } = props;

  const { reasoning, process, answer, text } = useMemo(
    () =>
      partitionBlocks({
        message,
        blocks: blocksOf(message),
        debugToolCalls,
      }),
    [message, debugToolCalls],
  );

  /**
   * `message.sources` is server-resolved and richer, but only lands after
   * persistence — during a live stream it is undefined, so the widget
   * falls back to deriving a source list from the blocks' own citation
   * annotations until the server-resolved array shows up.
   */
  const sources = useMemo(() => {
    const resolved = message.sources as RagSources | null | undefined;

    return resolved?.length
      ? resolved
      : sourcesFromAnnotations(blocksOf(message));
  }, [message]);
  const showTrail = process.length > 0 && (showToolActivity || debugToolCalls);

  return (
    <div className='flex w-full min-w-0 flex-col gap-1'>
      {showReasoning && reasoning ? (
        <ReasoningBlock reasoning={reasoning} />
      ) : null}

      {showTrail ? (
        <ProcessRegion
          tools={process}
          message={message}
          expandToolActivity={expandToolActivity}
          renderTrailStepInspector={renderTrailStepInspector}
        />
      ) : null}

      {answer.map(item => {
        if (item.kind === 'text') {
          return (
            <Message.BubbleGroup key='answer-text'>
              <Message.Bubble>
                <Markdown
                  citations={sources}
                  inlineCitations={inlineCitations}
                  streaming={!isMessageSettled(message.status)}
                >
                  {text}
                </Markdown>
                {!renderSources &&
                sourcesDisplayMode === SourcesDisplayMode.Pills &&
                sources ? (
                  <SourcesBlock
                    sources={sources}
                    sourcesFormat={sourcesFormat}
                    sourcesDisplayMode={SourcesDisplayMode.Pills}
                  />
                ) : null}
              </Message.Bubble>
              {answerFooter}
            </Message.BubbleGroup>
          );
        }

        if (item.kind === 'form') {
          /**
           * A form's state is whether anything answered its call, never a
           * constant: hardcoding `pending` re-rendered an already-answered
           * form as a blank submittable copy on every reload.
           */
          const { value, result } = item;

          return (
            <Fragment key={`form-${value.toolCallId}`}>
              {renderUnknownTool?.({
                toolName: value.name,
                toolCallId: value.toolCallId,
                args: {},
                content: result?.content,
                artifact: value.artifact,
                toolCallStatus: resultStatus(result),
                kind: 'form',
                state: result ? 'result' : 'call-complete',
              })}
            </Fragment>
          );
        }

        return (
          <Fragment key={`tool-${item.tool.id}`}>
            {renderToolCard({
              tool: item.tool,
              message,
              toolRegistry,
              renderUnknownTool,
              debugToolCalls,
            })}
          </Fragment>
        );
      })}

      {answer.some(item => item.kind === 'text') ? null : answerFooter}
      {renderSources?.(message)}
      {!renderSources &&
      sourcesDisplayMode === SourcesDisplayMode.Structured &&
      sources &&
      sources.length > 0 ? (
        <Message.BubbleGroup key='answer-sources'>
          <Message.Bubble>
            <SourcesBlock
              sources={sources}
              sourcesFormat={sourcesFormat}
              sourcesDisplayMode={SourcesDisplayMode.Structured}
            />
          </Message.Bubble>
        </Message.BubbleGroup>
      ) : null}
    </div>
  );
});

export const InterruptedNotice = memo(
  ({
    mode = 'stopped',
    onRetry,
  }: {
    mode?: 'stopped' | 'failed';
    onRetry?: () => void;
  }) => {
    const { t } = useLingui();
    const message =
      mode === 'failed' ? t`Response failed.` : t`Response stopped.`;

    if (!onRetry) {
      return (
        <p className='mt-1 text-xs text-chat-muted-foreground'>{message}</p>
      );
    }

    return (
      <div className='mt-1 flex items-center gap-2 text-xs text-chat-muted-foreground'>
        <span>{message}</span>
        <button
          type='button'
          onClick={onRetry}
          className='font-medium text-chat-foreground underline-offset-2 hover:underline'
        >
          {mode === 'failed' ? t`Try again` : t`Continue`}
        </button>
      </div>
    );
  },
);
